import { useState, useCallback, useEffect, useRef } from "react";
import { Copy, Check, Code2, FileImage } from "lucide-react";
import hljs from "highlight.js/lib/core";
import css from "highlight.js/lib/languages/css";
import type { SceneNode } from "@/document/model/nodes";
import type { CurvaDocument } from "@/document/model/document";
import { exportSceneNodeToSvg } from "@/export/svgExporter";
import { exportSceneNodeToPngBlob } from "@/export/pngExporter";
import {
  generateNodeCss,
  generateNodeTailwind,
  sectionsToCssString,
  generateFillsOnlyCss,
  generateStrokesOnlyCss,
  generateFillsTailwind,
  generateStrokesTailwind,
  generateEffectsCss,
  generateEffectsTailwind,
  generateTypographyCss,
  generateTypographyTailwind,
} from "./cssGenerator";
import { normalizeFillStack, normalizeStrokeStack } from "@/document/model/styles";
import { uiStore } from "@/editor/store";
import { downloadBytes } from "@/editor/shell/shellTransfer";
import { normalizedExportBaseName, pngScaleSuffix } from "@/editor/shell/shellExportNames";
import { PanelSection, INSPECTOR_SECTIONS_CLASS, PANEL_SECTION_TITLE_CLASS } from "./PanelSection";

hljs.registerLanguage("css", css);

function fmt(n: number): string {
  const r = Math.round(n * 100) / 100;
  return r === 0 ? "0" : String(r);
}

function useCopyToClipboard(text: string): [boolean, () => void] {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    }).catch(() => {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.cssText = "position:fixed;left:-9999px";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      el.remove();
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);

  useEffect(() => () => clearTimeout(timerRef.current), []);
  return [copied, copy];
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="shrink-0 text-[11px] text-slate-500 w-14">{label}</span>
      <span className="min-w-0 truncate text-right font-mono text-[11px] text-slate-300">{value}</span>
    </div>
  );
}

/** CSS block: syntax highlighted, horizontal scroll for wide lines */
function CodeBlock({ code }: { code: string }) {
  const [copied, copy] = useCopyToClipboard(code);
  const highlighted = hljs.highlight(code, { language: "css" }).value;

  return (
    <div className="group relative rounded border border-white/10 bg-[#12151a]">
      <button
        type="button"
        onClick={copy}
        title="Copy"
        className="absolute right-1.5 top-1.5 z-10 grid size-6 place-items-center rounded opacity-0 transition-opacity group-hover:opacity-100 hover:bg-white/10 text-slate-400 hover:text-slate-100"
      >
        {copied ? <Check className="size-3.5 text-sky-400" /> : <Copy className="size-3.5" />}
      </button>
      <pre
        className="overflow-x-auto p-2.5 pr-8 text-[11px] leading-relaxed font-mono"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: highlight.js sanitized output
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </div>
  );
}

/** Tailwind block: class chips that wrap, copy-on-click per chip + copy-all button */
function TailwindBlock({ classes }: { classes: string[] }) {
  const allText = classes.join(" ");
  const [allCopied, copyAll] = useCopyToClipboard(allText);

  return (
    <div className="group relative rounded border border-white/10 bg-[#12151a]">
      <button
        type="button"
        onClick={copyAll}
        title="Copy all"
        className="absolute right-1.5 top-1.5 z-10 grid size-6 place-items-center rounded opacity-0 transition-opacity group-hover:opacity-100 hover:bg-white/10 text-slate-400 hover:text-slate-100"
      >
        {allCopied ? <Check className="size-3.5 text-sky-400" /> : <Copy className="size-3.5" />}
      </button>
      {classes.length === 0 ? (
        <p className="p-2.5 text-[11px] font-mono text-slate-500">/* no classes */</p>
      ) : (
        <div className="flex flex-wrap gap-1 p-2.5 pr-8">
          {classes.map((cls, i) => (
            <TailwindChip key={`${cls}-${i}`} cls={cls} />
          ))}
        </div>
      )}
    </div>
  );
}

function TailwindChip({ cls }: { cls: string }) {
  const [copied, copy] = useCopyToClipboard(cls);
  return (
    <button
      type="button"
      onClick={copy}
      title="Copy class"
      className={`rounded px-1.5 py-0.5 font-mono text-[10px] transition-colors ${
        copied
          ? "bg-sky-500/20 text-sky-300"
          : "bg-white/[0.06] text-sky-200 hover:bg-white/[0.12] hover:text-sky-100"
      }`}
    >
      {copied ? "✓" : cls}
    </button>
  );
}

type DisplayMode = "css" | "tailwind";

/** Section that switches between a CSS block and a Tailwind block */
function StyleBlock({
  mode,
  cssCode,
  tailwindClasses,
}: {
  mode: DisplayMode;
  cssCode: string;
  tailwindClasses: string[];
}) {
  if (mode === "css") return <CodeBlock code={cssCode} />;
  return <TailwindBlock classes={tailwindClasses} />;
}

function ModeSwitcher({ mode, onChange }: { mode: DisplayMode; onChange: (m: DisplayMode) => void }) {
  return (
    <div className="flex items-center rounded bg-white/[0.04] p-0.5">
      {(["css", "tailwind"] as const).map(m => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`h-5 rounded px-2 text-[10px] font-medium uppercase transition-colors ${
            mode === m ? "bg-sky-500/20 text-sky-100" : "text-slate-400 hover:text-slate-200"
          }`}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

interface ExportButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  loading?: boolean;
}

function ExportButton({ icon, label, onClick, loading }: ExportButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="flex flex-1 items-center justify-center gap-1.5 rounded border border-white/10 bg-white/[0.04] px-2 py-1.5 text-[10px] text-slate-300 transition-colors hover:bg-white/[0.08] hover:text-slate-100 disabled:opacity-50"
    >
      {icon}
      {label}
    </button>
  );
}

interface DevPanelProps {
  node: SceneNode;
  document: CurvaDocument;
}

export function DevPanel({ node, document }: DevPanelProps) {
  const [mode, setMode] = useState<DisplayMode>("css");
  const [svgLoading, setSvgLoading] = useState(false);
  const [pngLoading, setPngLoading] = useState<number | undefined>(undefined);
  const [exportError, setExportError] = useState<string | undefined>(undefined);

  const sections = generateNodeCss(node);
  const fullCss = sectionsToCssString(sections);
  const tailwindClasses = generateNodeTailwind(node);

  const fills = normalizeFillStack(node.style);
  const strokes = normalizeStrokeStack(node.style);
  const effects = (node.style.effects ?? []).filter(e => e.visible);
  const hasFills = fills.length > 0;
  const hasStrokes = strokes.length > 0;
  const hasEffects = effects.length > 0;
  const isText = node.type === "text";

  const { x, y, width, height } = node.bounds;

  const handleExportSvg = useCallback(async () => {
    setSvgLoading(true);
    setExportError(undefined);
    try {
      const svg = await exportSceneNodeToSvg(node);
      const result = await downloadBytes(
        new TextEncoder().encode(svg),
        `${normalizedExportBaseName(node.name)}.svg`,
        "image/svg+xml;charset=utf-8",
      );
      if (result.method === "cancelled") {
        uiStore.setMessage("SVG export cancelled");
      }
    } catch (e) {
      setExportError(e instanceof Error ? e.message : "SVG export failed");
    } finally {
      setSvgLoading(false);
    }
  }, [node]);

  const handleExportPng = useCallback(async (scale: number) => {
    setPngLoading(scale);
    setExportError(undefined);
    try {
      const blob = await exportSceneNodeToPngBlob(document, node, scale);
      const result = await downloadBytes(
        new Uint8Array(await blob.arrayBuffer()),
        `${normalizedExportBaseName(node.name)}${pngScaleSuffix(scale)}.png`,
        "image/png",
      );
      if (result.method === "cancelled") {
        uiStore.setMessage("PNG export cancelled");
      }
    } catch (e) {
      setExportError(e instanceof Error ? e.message : "PNG export failed");
    } finally {
      setPngLoading(undefined);
    }
  }, [document, node]);

  return (
    <div className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain p-3 ${INSPECTOR_SECTIONS_CLASS}`}>

      {/* Info */}
      <PanelSection title="Info">
        <div className="space-y-0">
          <InfoRow label="Width" value={`${fmt(width)}px`} />
          <InfoRow label="Height" value={`${fmt(height)}px`} />
          <InfoRow label="X" value={`${fmt(x)}px`} />
          <InfoRow label="Y" value={`${fmt(y)}px`} />
          <InfoRow label="Type" value={node.type} />
        </div>
      </PanelSection>

      {/* All styles */}
      <PanelSection
        title={<span className={PANEL_SECTION_TITLE_CLASS}>Styles</span>}
        headerRight={<ModeSwitcher mode={mode} onChange={setMode} />}
      >
        <StyleBlock mode={mode} cssCode={fullCss} tailwindClasses={tailwindClasses} />
      </PanelSection>

      {/* Fills */}
      {hasFills && (
        <PanelSection title="Fill">
          <StyleBlock
            mode={mode}
            cssCode={generateFillsOnlyCss(node)}
            tailwindClasses={generateFillsTailwind(node)}
          />
        </PanelSection>
      )}

      {/* Strokes */}
      {hasStrokes && (
        <PanelSection title="Stroke">
          <StyleBlock
            mode={mode}
            cssCode={generateStrokesOnlyCss(node)}
            tailwindClasses={generateStrokesTailwind(node)}
          />
        </PanelSection>
      )}

      {/* Effects */}
      {hasEffects && (
        <PanelSection title="Effects">
          <StyleBlock
            mode={mode}
            cssCode={generateEffectsCss(node)}
            tailwindClasses={generateEffectsTailwind(node)}
          />
        </PanelSection>
      )}

      {/* Typography */}
      {isText && (
        <PanelSection title="Typography">
          <StyleBlock
            mode={mode}
            cssCode={generateTypographyCss(node)}
            tailwindClasses={generateTypographyTailwind(node)}
          />
        </PanelSection>
      )}

      {/* Export */}
      <PanelSection title="Export">
        <div className="flex flex-col gap-1.5">
          <ExportButton
            icon={<Code2 className="size-3" aria-hidden="true" />}
            label={svgLoading ? "…" : "SVG"}
            onClick={handleExportSvg}
            loading={svgLoading}
          />
          {([1, 2, 3] as const).map(scale => (
            <ExportButton
              key={scale}
              icon={<FileImage className="size-3" aria-hidden="true" />}
              label={pngLoading === scale ? "…" : `PNG${scale > 1 ? ` @${scale}x` : ""}`}
              onClick={() => handleExportPng(scale)}
              loading={pngLoading !== undefined}
            />
          ))}
        </div>
        {exportError && (
          <p className="mt-1.5 text-[10px] text-red-400">{exportError}</p>
        )}
      </PanelSection>
    </div>
  );
}

export function DevPanelEmpty() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
      <Code2 className="size-6 text-slate-600" aria-hidden="true" />
      <p className="text-[11px] text-slate-500">Select an object to inspect its styles</p>
    </div>
  );
}
